import CTNButton from '@ctn/ui/components/ctn-button';
import CTNButtonsGroup from '@ctn/ui/components/ctn-buttons-group';
import CTNInput from '@ctn/ui/components/ctn-input';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import { useEffect, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';

import DocViewer from '@/components/DocViewer';
import FieldDisplay from '@/components/FieldDisplay';
import PageHeader from '@/components/PageHeader';
import { useGlobalContext } from '@/context/global.context';
import { usePageContext } from '@/context/page.context';
import useApiData from '@/hooks/useApiData';
import useFetchDocument from '@/hooks/useFetchDocument';
import { usePdfViewer } from '@/hooks/usePdfViewer';
import useRouteTramite from '@/hooks/useRouteTramite';
import { getDetailSubsanaciones } from '@/services/tramite.service';

import { ROUTES_LABEL } from '@/constants/routes.constants';
import { SUBSANACION_ESTADO_COLOR } from '@/constants/tramite.constants';
import { getBreadcrumbTramite } from '@/helpers/navigation';
import { notificationsConfig } from '@/helpers/notifications';
import { getIconDocumento } from '@/helpers/upload';
import { type ISolicitudDocumentacion } from '@/interfaces/model/don.interface';

import { defaultValues, type Form } from './form';

const TramiteSolicitudDocumentacionDetalle = (): ReactElement => {
	const { idTramite, idTipoSubsanacion, idSubsanacion } = useParams();

	const { user, getTramiteData } = useGlobalContext();
	const ROUTES_TRAMITE = useRouteTramite();
	const { appendBreadcrumb, onBackClick } = usePageContext();
	const { triggerFetch } = useApiData({ loading: ['detail', 'subsanar'] as const });
	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });
	const location = useLocation();
	const { from } = location.state || {};

	const { fetchDocument } = useFetchDocument({ idTramite: Number(idTramite) });
	const { isLoading, url, base64, loadPdf } = usePdfViewer();

	const { control, reset, watch } = useForm<Form>({ defaultValues, mode: 'onChange' });

	const documento = watch('documentoSubsanado');
	const estado = watch('estado');

	const mapData = (data: ISolicitudDocumentacion): Form => {
		const {
			tramite,
			id,
			tipo,
			fechaCreacion,
			fechaSubsanacion,
			observacionesPeticion,
			observacionesSubsanacion,
			estado,
			documentoSubsanado,
			tipoDocumento,
		} = data;

		return {
			...defaultValues,
			nifEmisor: tramite?.nifEmisor,
			idError: id,
			estado,
			fechaSubsanacion,
			idTramite: tramite?.id,
			idTipoError: tipo?.id,
			numeroTramite: tramite.numeroTramite,
			fechaCreacion,
			observacionesPeticion,
			observacionesSubsanacion,
			documentoSubsanado,
			tipoDocumento: tipoDocumento.descripcion,
		};
	};

	const getData = async () => {
		if (!user || !idTramite || !idTipoSubsanacion || !idSubsanacion) {
			openNotification(
				notificationTemplate.errorMessage('No se pudo cargar la información necesaria para el detalle.'),
			);
			return;
		}

		const response = await triggerFetch({
			fetch: getDetailSubsanaciones,
			payload: {
				nifEmisor: user?.nifEmisor,
				idTramite: Number(idTramite),
				idTipoSector: user?.tipoSectorEmisor.id,
				idTipoSubsanacion: Number(idTipoSubsanacion),
				idSubsanacion: Number(idSubsanacion),
			},
			name: 'detail',
		});

		const data = response.data as ISolicitudDocumentacion;

		const getPdfSource = async () => {
			const id = data?.documentoSubsanado?.id;
			return id ? (await fetchDocument(id))?.data : undefined;
		};

		loadPdf(getPdfSource);

		if (data) {
			reset(mapData(data));
		}
	};

	useEffect(() => {
		if (from) {			
			appendBreadcrumb('Solicitud Documentación', [
				from,
				{ label: ROUTES_LABEL.SOLICITUDES, url: ROUTES_TRAMITE.SOLICITUDES },
				{ label: ROUTES_LABEL.SOLICITUD, url: -1 },
				{ label: 'Solicitud Documentación', url: location.pathname },
			]);
		} else {
			appendBreadcrumb('Detalle', [
				getBreadcrumbTramite(getTramiteData()),
				{
					label: ROUTES_LABEL.SOLICITUDES_DOCUMENTACION,
					url: ROUTES_TRAMITE.SOLICITUDES_DOCUMENTACION_ATENDIDAS,
				},
				{ label: 'Detalle', url: location.pathname },
			]);
		}
		getData();
	}, []);

	return (
		<>
			<PageHeader hasHelp title={ROUTES_LABEL.SOLICITUDES_DOCUMENTACION_DETALLE} className="u-mt-8" />
			<div className="[ c-pnp-box c-pnp-box--border c-pnp-box--breakout ] [ u-mt-24 u-flow-border ]">
				<div className="c-pnp-box__body">
					<div className="[ o-grid ] [ c-pnp-form-group c-pnp-form-group--accent c-pnp-form-group--radius c-pnp-form-group--display-only ] [ u-flow ]">
						<div className="o-grid__row">
							<CTNInput
								noFeedBack
								condensed
								displayOnly
								type="text"
								classNameField="o-grid__col-12 o-grid__col-3@portrait"
								name="fechaCreacion"
								label="Fecha alta"
								control={control}
							/>
							<CTNInput
								noFeedBack
								condensed
								displayOnly
								type="text"
								classNameField="o-grid__col-12 o-grid__col-3@portrait"
								name="numeroTramite"
								label="Nº de solicitud"
								control={control}
							/>
							<CTNInput
								noFeedBack
								condensed
								displayOnly
								type="text"
								classNameField="o-grid__col-12  o-grid__col-3@portrait"
								name="tipoDocumento"
								label="Tipo"
								control={control}
							/>
							<FieldDisplay
								noFeedBack
								condensed
								displayOnly
								className="o-grid__col-12 o-grid__col-3@portrait"
								name="estado.descripcion"
								label="Estado"
								iconLeft={{
									icon: 'status',
									color:
										SUBSANACION_ESTADO_COLOR[estado?.id as keyof typeof SUBSANACION_ESTADO_COLOR] ??
										'gray-50',
								}}
								control={control}
							/>
						</div>
						<div className="o-grid__row">
							<CTNInput
								noFeedBack
								condensed
								displayOnly
								type="text"
								classNameField="o-grid__col-12"
								name="observacionesPeticion"
								label="Motivo de la solicitud"
								control={control}
							/>
						</div>
					</div>
					<div className="[ o-grid ] [ c-pnp-form-group c-pnp-form-group--display-only ] [ u-flow ] [ u-mt-24 ]">
						{documento && (
							<div className="o-grid__row">
								<CTNInput
									noFeedBack
									condensed
									displayOnly
									type="text"
									classNameField="[ o-grid__col-12 o-grid__col-4@portrait ]"
									name="fechaSubsanacion"
									label="Fecha de envío"
									control={control}
								/>
								<CTNInput
									noFeedBack
									condensed
									displayOnly
									type="text"
									classNameField="[ o-grid__col-12 o-grid__col-4@portrait ]"
									name="documentoSubsanado.nombre"
									label="Documento"
									control={control}
									iconLeft={{ icon: getIconDocumento(documento) }}
								/>
							</div>
						)}
						<div className="o-grid__row">
							<CTNInput
								noFeedBack
								condensed
								displayOnly
								classNameField="[ o-grid__col-12 ]"
								type="text"
								name="observacionesSubsanacion"
								label="Comentario del emisor"
								control={control}
							/>
						</div>
					</div>
					{documento && base64 && (
						<DocViewer
							mimeType={documento.mimeType.nombre}
							className="u-mt-24"
							loading={isLoading}
							url={url}
							fileIcon={getIconDocumento(documento)}
							fileName={documento.nombre}
						/>
					)}
				</div>
				<footer className="c-pnp-box__footer">
					<CTNButtonsGroup align="right">
						<CTNButton
							label="Cerrar"
							mode="tertiary"
							minWidth="140"
							size="md"
							onClick={() => onBackClick()}
						/>
					</CTNButtonsGroup>
				</footer>
			</div>
		</>
	);
};

export default TramiteSolicitudDocumentacionDetalle;
